import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from "i18next";
import logo from "../logo_UK.svg";
import { formatDateTime, formatDeliveryDate } from '../utils/utils';

function Report() {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const account = useRef(searchParams.get('account'));
  const id = useRef(searchParams.get('id'));
  const language = useRef(searchParams.get('language')) || 'en';
  const currentLanguage = language.current;

  const [serverData, setServerData] = useState(null);
  const [serverStyles, setServerStyles] = useState(null);
  const [error, setError] = useState(false)

  useEffect(() => {
    if (currentLanguage && ['en', 'he'].includes(currentLanguage)) {
      i18next.changeLanguage(currentLanguage);
    } else {
      i18next.changeLanguage('en');
    }
  }, [currentLanguage]);

  useEffect(() => {
    // prod: https://api.delivapp.com/consumer/api/
    // stage: https://api-staging.delivapp.com/consumer/api/
    if (account.current && id.current) {
      fetch(`https://api-staging.delivapp.com/consumer/api/receipt/${account.current}/${id.current}/${language.current}`)
        .then(res => res.json())
        .then(data => {
          if (data?.Message?.includes('An error has occurred')) {
            throw new Error();
          }
          setServerData({ ...data?.Order, title: data?.Title, logo: data?.LogoUrl })
          if (data?.LayoutConfiguration) setServerStyles(data?.LayoutConfiguration)
        })
        .catch((err) => {
          setError(true);
        });
    } else {
      setError(true);
    }
  }, [language]);

  useEffect(() => {
    const dir = currentLanguage === 'he' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
  }, [currentLanguage, serverData]);

  if (error) {
    return <div className="info-block">
      <p>{t("error")}</p>
    </div>;
  }

  if (!serverData) {
    return <div className="info-block">
      <p>{t("loading")}</p>
    </div>;
  }

  const renderHalfOption = (halfOpt) => {
    const halfOptionLeft = "◐"; // U+25D0
    const halfOptionRight = "◑"; // U+25D1

    if (halfOpt === 1) return halfOptionLeft;
    if (halfOpt === 2) return halfOptionRight;
    return '';
  }

  const renderCurrencySymbol = (currencyCode) => {
    if (currencyCode === 'ILS') return '₪';
    if (currencyCode === 'USD') return '$';
    if (currencyCode === 'EUR') return '€';
    return '';
  };

  const returnDiscountType = (discount) => {
    if (discount === 0) return t('discount');
    if (discount === 1) return t('bonus');
  }

  const renderLogo = () => (
    <div
      style={{
        width: "230px",
        height: "230px",
        margin: "0 auto",
        textAlign: "center"
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <img
          src={serverData?.logo || logo}
          alt="Logo"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain"
          }}
        />
      </div>
    </div>
  );

  function isRenderLogo() {
    if (serverStyles && Object.prototype.hasOwnProperty.call(serverStyles, 'PrintLogo') && !!serverStyles?.PrintLogo) {
      return renderLogo();
    } else if (serverStyles && !Object.prototype.hasOwnProperty.call(serverStyles, 'PrintLogo')) {
      return renderLogo();
    } else if (!serverStyles) {
      return renderLogo();
    } else {
      return null;
    }
  }

  const returnPaymentMethod = (paymentMethod, payment) => {
    if (paymentMethod === 1) {
      if (payment?.is_apple_pay) return 'Apple Pay';
      if (payment?.is_google_pay) return 'Google Pay';
      if (payment?.card_type?.length) return `${payment?.card_type} ${payment?.paymethod_id === 1 && !!payment?.card_mask ? `(${payment?.card_mask?.slice(-4)})` : ''}`;
      return `${t('card')} ${payment?.paymethod_id === 1 && !!payment?.card_mask ? `(${payment?.card_mask?.slice(-4)})` : ''}`
    };
    if (paymentMethod === 2) return t('cash');
    if (paymentMethod === 5) return t('bit');
  }

  function renderPaymentsBlock(payments) {
    if (!payments) return;

    const filteredPayments = (payments || []).filter((p) =>
      [1, 2, 5].includes(p.paymethod_id)
    );

    if (filteredPayments.length === 0) {
      return null;
    }

    return (
      <div>
        {filteredPayments.map((payment, i) => (
          <div key={i}>
            <p
              style={{
                padding: 0,
                fontWeight: "bold",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 30 }}>{t('paymentMethod')}</span>
              <span style={{ fontSize: 30 }}>{returnPaymentMethod(payment.paymethod_id, payment)}</span>
            </p>
            <p
              style={{
                padding: 0,
                fontWeight: "bold",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 30 }}>{t('paymentSummary')}</span>
              <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                ? `${renderCurrencySymbol(serverData?.currency)} ${payment.price}`
                : `${payment.price} ${renderCurrencySymbol(serverData?.currency)}`}
              </span>
            </p>
          </div>
        ))}
      </div>
    );
  }

  function buildStyles(serverStyles = {}, defaultStyles = {}, standardStyles = {}) {
    const finalStyles = { ...standardStyles };


    const sizeFromServer = serverStyles?.FontSize;
    const sizeFromDefault = defaultStyles?.FontSize;

    const mapFontSize = (value) => {
      if (value === 'Small') return 30;
      if (value === 'Medium') return 40;
      if (value === 'Large') return 60;
      return undefined;
    };

    let resolvedFontSize = undefined;
    if (typeof sizeFromServer !== 'undefined') {
      resolvedFontSize = mapFontSize(sizeFromServer);
    } else if (typeof sizeFromDefault !== 'undefined') {
      resolvedFontSize = sizeFromDefault;
    }
    if (resolvedFontSize) {
      finalStyles.fontSize = resolvedFontSize;
    }

    const italicFromServer = serverStyles?.Italic;
    const italicFromDefault = defaultStyles?.Italic;
    let resolvedItalic = undefined;
    if (typeof italicFromServer === 'boolean') {
      resolvedItalic = italicFromServer;
    } else if (typeof italicFromDefault === 'boolean') {
      resolvedItalic = italicFromDefault;
    }
    if (resolvedItalic === true) {
      finalStyles.fontStyle = 'italic';
    }


    const boldFromServer = serverStyles?.Bold;
    const boldFromDefault = defaultStyles?.Bold;

    let resolvedBold = undefined;
    if (typeof boldFromServer === 'boolean') {
      resolvedBold = boldFromServer;
    } else if (typeof boldFromDefault === 'boolean') {
      resolvedBold = boldFromDefault;
    }
    if (resolvedBold === true) {
      finalStyles.fontWeight = 'bold';
    }

    return finalStyles;
  }

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        backgroundColor: "#F5F5F5",
        fontFamily: "Arial, sans-serif"
      }}
    >
      <div
        style={{
          width: "668px",
          margin: "0 auto",
          backgroundColor: "#FFFFFF",
          paddingTop: "50px",
          paddingBottom: "50px"
        }}
      >
        <div style={{ width: "568px", margin: "0 auto" }}>

          {isRenderLogo()}

          <div
            style={{
              width: "568px",
              border: "3px solid #000",
              margin: "12px 0 12px 0",
              borderRadius: "50px",
              display: "flex",
              textAlign: "center",
              justifyContent: "center",
              marginTop: 24,
              marginBottom: 24,
            }}
          >
            <h2 style={{ margin: 0, padding: 0, fontSize: 60 }}>{serverData?.sequence_id || '000'}</h2>
          </div>

          <div
            style={{
              width: "568px",
              borderBottom: "3px dashed #000",
              margin: "12px 0 12px 0"
            }}
          >
            <h2 style={{ margin: 0, padding: 0, fontSize: 60 }}>{serverData?.title || 'Title'}</h2>
          </div>

          {serverData?.delivery_type === 1 ? <div
            style={{
              width: "568px",
              borderBottom: "3px dashed #000",
              marginTop: "12px",
              marginBottom: "12px"
            }}
          >
            <p style={{ padding: 0, display: "flex", flexDirection: "column" }}>
              <span>
                <span style={buildStyles(null, { FontSize: 40, Italic: true, title: true })} >{t('address')}:</span> <span style={{ fontSize: 40, fontStyle: "italic" }}>{serverData?.city ? `${serverData?.city},` : ''}{serverData?.address}</span>
              </span>
              {serverData?.storey?.length ? <span>
                <span style={buildStyles(null, { FontSize: 30, Italic: true, title: true })}>{t('storey')}:</span> <span style={{ fontSize: 30, fontStyle: "italic" }}>{serverData?.storey}</span>
              </span> : null}
              {serverData?.flat?.length ? <span>
                <span style={buildStyles(null, { FontSize: 30, Italic: true, title: true })}>{t('flat')}:</span> <span style={{ fontSize: 30, fontStyle: "italic" }}>{serverData?.flat}</span>
              </span> : null}
            </p>
          </div> : null}

          {serverData?.delivery_type && serverData?.delivery_type !== 1 ? <div
            style={{
              width: "568px",
              borderBottom: "3px dashed #000",
              marginTop: "12px",
              marginBottom: "12px"
            }}
          >
            <p style={{ padding: 0 }}>
              <span style={buildStyles(null, { FontSize: 30, Bold: true, title: true })}>{t('deliveryMethod')}: </span>
              <span style={{ fontSize: 40 }}>{
                serverData?.delivery_type === 2 ? t('takeaway') : serverData?.delivery_type === 3 ? t('email') : t('dineIn')
              }</span>
            </p>
          </div> : null}

          <div
            style={{
              width: "568px",
              borderBottom: "3px dashed #000",
              marginTop: "12px",
              marginBottom: "12px"
            }}
          >
            <p style={{ padding: 0 }}>
              <span style={buildStyles(null, { FontSize: 30, Italic: true, title: true })}>{t('customerDetails')}:</span>
              <br />
              <span style={{ fontSize: 40, fontStyle: "italic" }}>{serverData?.customer_name}{serverData?.company_name ? `[${serverData?.company_name}]` : ''}</span>
              {serverData?.phone
                ? (<><br /><span style={{ fontSize: 30, fontStyle: "italic" }}>{serverData?.phone}</span></>)
                : null}
            </p>
          </div>

          <div
            style={{
              width: "568px",
              borderBottom: "3px dashed #000",
              marginTop: "12px",
              marginBottom: "12px"
            }}
          >
            <p style={{ padding: 0 }}>
              <span style={buildStyles(null, { FontSize: 30, Bold: true, title: true })}>{t('type')}: </span>
              <span style={{ fontSize: 40 }}>{
                serverData?.delivery_strategy === 0
                  ? t('ASAP') : serverData?.delivery_datetime
                    ? formatDeliveryDate(serverData?.delivery_datetime, currentLanguage)
                    : t('concreteTime')}
              </span>
            </p>
          </div>

          {serverData?.products?.length ?
            serverData?.products?.map((item, i) => (
              <div
                key={i}
                style={{
                  width: "568px",
                  marginTop: "32px",
                  marginBottom: "24px"
                }}
              >
                <p style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 40, padding: "0 2px", fontWeight: "bold",  direction: "ltr" }}>
                    {item?.actual_weight > 0 && currentLanguage === 'he' ? (`(gr${item?.actual_weight}) `) : ''}
                    {item?.name?.replace(/\p{Extended_Pictographic}/gu, '')}
                    {item?.actual_weight > 0 && currentLanguage !== 'he' ? (` (${item?.actual_weight}gr)`) : ''}
                  </span>
                  <span style={{ fontSize: 40, whiteSpace: "nowrap", direction: "ltr" }}>{currentLanguage === 'he' ? `${item?.quantity} x` : `x ${item?.quantity}`}</span>
                </p>
                {item?.comment?.length
                  ? <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                    <span style={{ fontSize: 30, fontStyle: "italic" }}>{t('comment')}: </span>
                    <span style={{ fontSize: 30 }}>{item?.comment}</span>
                  </div>
                  : null}
                {item?.name_on_box?.length
                  ? <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                    <span style={{ fontSize: 30, fontStyle: "italic" }}>{t('name_on_box')}: </span>
                    <span style={{ fontSize: 30 }}>{item?.name_on_box}</span>
                  </div>
                  : null}
                {
                  item?.options?.length
                    ? (
                      item?.options?.map((option, i) => (
                        <div
                          key={i}
                          style={{
                            border: "2px solid #000",
                            padding: "12px 30px",
                            marginBottom: "12px"
                          }}
                        >
                          <p style={{ margin: 0, padding: 0, fontSize: 30 }}>
                            {option?.name?.replace(/\p{Extended_Pictographic}/gu, '')}
                          </p>
                          {option?.suboptions?.length
                            ? (<ul style={{ margin: currentLanguage === 'he' ? "0 25px 0 0" : "0 0 0 25px", padding: 0, fontSize: 22 }}>
                              {option?.suboptions?.map((subopt, i) => (
                                <li
                                  key={i}
                                  style={{
                                    listStyleType: "disc",
                                    listStylePosition: "outside",
                                    display: "list-item",
                                    marginBottom: "8px"
                                  }}
                                >
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ fontStyle: "italic" }}>
                                      {subopt?.name?.replace(/\p{Extended_Pictographic}/gu, '')}
                                    </span>
                                    <span style={{ fontSize: 35, marginTop: "-5px" }}>
                                      {renderHalfOption(subopt?.half_option)}
                                    </span>
                                    {subopt?.quantity > 1
                                      ? <span style={{ fontSize: 30, whiteSpace: "nowrap", direction: "ltr" }}>{currentLanguage === 'he' ? `${subopt?.quantity} x` : `x ${subopt?.quantity}`}</span>
                                      : null}
                                  </div>
                                </li>
                              ))}
                            </ul>)
                            : null}
                        </div>
                      ))
                    )
                    : null
                }

              </div>
            )) : null
          }

          {serverData?.cutlery > 0
            ? (<div
              style={{
                width: "568px",
                marginTop: "32px",
                marginBottom: "24px"
              }}
            >
              <p style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 40, padding: "0 2px", fontWeight: "bold" }}>{t("cutlery")}</span>
                <span style={{ fontSize: 40, whiteSpace: "nowrap", direction: "ltr" }}>{currentLanguage === 'he' ? `${serverData?.cutlery} x` : `x ${serverData?.cutlery}`}</span>
              </p>
            </div>)
            : null
          }

          {
            !!serverData?.comment
              ? <div
                style={{
                  width: "568px",
                  marginTop: "34px",
                  marginBottom: "12px"
                }}
              >
                <p style={{ padding: 0 }}>
                  <span style={buildStyles(null, { FontSize: 50, Italic: true, title: true })}>{t('notes')}:</span>
                  <br />
                  <span style={{ fontSize: 30, fontStyle: "italic" }}>{serverData?.comment}</span>
                </p>
              </div>
              : null
          }

          {serverData?.discounts?.length
            ? (
              <div style={{ width: "568px", marginTop: "12px", borderTop: "3px dashed #000", marginBottom: "12px", borderBottom: "3px dashed #000", }}>
                {serverData?.discounts?.map((item, i) => (
                  <p key={i} style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 30 }}>{returnDiscountType(item?.discount_type)}{(item?.discount_type === 0 && item?.offer_name?.length) ? ` "${item?.offer_name}"` : null}</span>
                    <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                      ? `${renderCurrencySymbol(serverData?.currency)} ${item?.discount}-`
                      : `-${item?.discount} ${renderCurrencySymbol(serverData?.currency)}`}
                    </span>
                  </p>
                ))}
              </div>
            )
            : null
          }


          <div style={{ width: "568px", marginTop: "12px", marginBottom: "12px", borderTop: !serverData?.discounts?.length ? '3px dashed #000' : '' }}>
            {renderPaymentsBlock(serverData?.payments)}

            {!!serverData?.topup_to_minimum && serverData?.topup_to_minimum !== 0
              ? (<p style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 30 }}>{t('topupToMinimum')}</span>
                <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                  ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.topup_to_minimum}`
                  : `${serverData?.topup_to_minimum} ${renderCurrencySymbol(serverData?.currency)}`}
                </span>
              </p>)
              : null}

            {!!serverData?.driver_tip && serverData?.driver_tip !== 0
              ? (<p style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 30 }}>{t('driverTip')}</span>
                <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                  ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.driver_tip}`
                  : `${serverData?.driver_tip} ${renderCurrencySymbol(serverData?.currency)}`}
                </span>
              </p>)
              : null}

            {!!serverData?.service_fee && serverData?.service_fee !== 0
              ? (<p style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 30 }}>{t('serviceFee')}</span>
                <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                  ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.service_fee}`
                  : `${serverData?.service_fee} ${renderCurrencySymbol(serverData?.currency)}`}
                </span>
              </p>)
              : null}


            {!!serverData?.weight_adjustment_price_buffer && serverData?.weight_adjustment_price_buffer !== 0
              ? (<p style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 30 }}>{t('possible_extra')}</span>
                <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                  ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.weight_adjustment_price_buffer}`
                  : `${serverData?.weight_adjustment_price_buffer} ${renderCurrencySymbol(serverData?.currency)}`}
                </span>
              </p>)
              : null}

            {(serverData?.delivery_type !== 2 && serverData?.delivery_type !== 4)
              ? <p style={{ padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 30 }}>{t('price')}</span>
                <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                  ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.delivery_price}`
                  : `${serverData?.delivery_price} ${renderCurrencySymbol(serverData?.currency)}`}
                </span>
              </p> : null}
            <p style={{ margin: 0, padding: 0, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 30 }}>{t('total')}</span>
              <span style={{ fontSize: 30, direction: "ltr" }}>{currentLanguage === 'he'
                ? `${renderCurrencySymbol(serverData?.currency)} ${serverData?.full_price}`
                : `${serverData?.full_price} ${renderCurrencySymbol(serverData?.currency)}`}
              </span>
            </p>
          </div>

          <div style={{ width: "568px", borderTop: "2px dashed #000", marginTop: "12px", paddingTop: "12px", marginBottom: "12px" }}>
            <p style={{ padding: 0, margin: 0, fontSize: 30, fontWeight: "bold" }}>
              {formatDateTime(serverData?.creation_datetime, currentLanguage)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Report;
